/**
 * Activité des médecins (décision 165) — combien de patients chaque
 * praticien a reçus, par agence et par jour, et la durée moyenne de ses
 * consultations.
 *
 * Des comptes et des durées, rien d'autre : ni patient, ni visite, ni motif.
 * La prestation n'apparaît que comme clé de regroupement (décision 218).
 * Le schéma de réponse est strict pour qu'un champ ajouté par
 * mégarde côté serveur échoue au test plutôt que de partir à l'écran.
 */
import { z } from 'zod';
/**
 * Un jour civil qui existe : `2026-13-45` ou `2026-02-30` ont la forme d'une
 * date sans en être une, et rebasculeraient en silence (ou en erreur 500).
 */
const jour = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .refine((valeur) => {
    const date = new Date(`${valeur}T00:00:00Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === valeur;
}, 'Date inexistante.');
/** 30 jours glissants au plus : Aujourd'hui, 7 jours, 30 jours (décision 165). */
export const PERIODE_MAX_ACTIVITE_JOURS = 30;
/** Au-delà, la consultation a été oubliée ouverte : elle n'entre pas dans la moyenne. */
export const DUREE_MAX_CONSULTATION_MINUTES = 180;
/**
 * Au-delà, l'attente (arrivée → appel) n'est pas une attente : visite restée
 * de la veille, patient parti puis revenu. Elle n'entre pas dans la moyenne.
 */
export const ATTENTE_MAX_MINUTES = 240;
/** En dessous de ce nombre de durées mesurées, pas de moyenne (« — »). */
export const MESURES_MIN_POUR_MOYENNE = 3;
/**
 * Le médecin ne lit que la journée en cours et sa spécialité dans son
 * centre (décision du TL, 28/10) : ni 7 ni 30 jours, ni vue par spécialité
 * ou par centre. Le responsable de site, le médecin chef et la Direction
 * gardent tout.
 */
export const ROLES_ACTIVITE_RESTREINTE = ['MEDECIN'];
export const schemaFiltresActiviteMedecins = z.object({
    du: jour,
    au: jour,
    /** Transverse seulement ; un rôle de site est ramené à son agence. */
    siteId: z.uuid().optional(),
});
const schemaJourActivite = z.strictObject({ jour, recus: z.number().int() });
export const schemaLigneActiviteMedecin = z.strictObject({
    praticienId: z.uuid(),
    praticienNom: z.string(),
    siteId: z.uuid(),
    siteNom: z.string(),
    /** Patients pris en charge sur la période, consultations en cours comprises. */
    recus: z.number().int(),
    /** En minutes, une décimale ; `null` sous `MESURES_MIN_POUR_MOYENNE` durées. */
    dureeMoyenneMin: z.number().nullable(),
    /** Reçus sans durée retenue : en cours, oubliés ouverts, durée aberrante. */
    sansDuree: z.number().int(),
    /** Un élément par jour de la période, jours vides compris. */
    parJour: z.array(schemaJourActivite),
    /** Jours de la période où le médecin a reçu au moins un patient. */
    joursTravailles: z.number().int(),
    /** `recus / joursTravailles`, une décimale (pas les jours du calendrier). */
    recusParJourTravaille: z.number(),
});
/**
 * L'attente (arrivée → appel) se rattache au centre et à la spécialité, pas
 * au médecin : la file est commune (décision 218). Hors ressaisies après
 * coupure, patients mis de côté et attentes de plus de `ATTENTE_MAX_MINUTES`.
 */
const attente = {
    /** En minutes, une décimale ; `null` sous `MESURES_MIN_POUR_MOYENNE` attentes. */
    attenteMoyenneMin: z.number().nullable(),
    /** Attentes retenues dans la moyenne. */
    attentesMesurees: z.number().int(),
};
/** La ligne repère d'une agence : pas un classement, un point de comparaison. */
export const schemaRepereAgence = z.strictObject({
    siteId: z.uuid(),
    siteNom: z.string(),
    medecins: z.number().int(),
    /** Patients reçus par médecin en moyenne, une décimale. */
    recusParMedecin: z.number(),
    /** Reçus de l'agence / jours travaillés de ses médecins, une décimale. */
    recusParJourTravaille: z.number(),
    dureeMoyenneMin: z.number().nullable(),
    sansDuree: z.number().int(),
    ...attente,
});
/** Une spécialité (la prestation de la visite) dans une agence. */
export const schemaSpecialiteAgence = z.strictObject({
    siteId: z.uuid(),
    siteNom: z.string(),
    /**
     * La prestation de la visite, en majuscules ; regroupée sans accent ni
     * casse (décision 221), affichée dans sa graphie la plus reçue.
     */
    specialite: z.string(),
    medecins: z.number().int(),
    recus: z.number().int(),
    recusParJourTravaille: z.number(),
    dureeMoyenneMin: z.number().nullable(),
    sansDuree: z.number().int(),
    ...attente,
});
export const schemaActiviteMedecins = z.strictObject({
    du: jour,
    au: jour,
    lignes: z.array(schemaLigneActiviteMedecin),
    reperes: z.array(schemaRepereAgence),
    specialites: z.array(schemaSpecialiteAgence),
});
//# sourceMappingURL=activite-medecins.js.map